/**
 * 「提交前快速结构化」· 前后端**同一份契约真本**（2026-08-27 主公定）。
 *
 * 与 `amend-contract.ts` / `respondent-contract.ts` 同一个用法：
 * 前端走相对路径直接 import 本文件，**不做镜像副本**。
 * 本项目已经吃过一次「前后端各自定义契约」的亏（五处分歧，PROGRESS 有记录）。
 *
 * ════════ 这条路是干什么的 ════════
 *
 * 改前：市民按下「提交」→ 落乐享一行「待受理」→ **提交之后**异步跑整包结构化
 *       （中位 ~25 秒）→ 案件领域／投诉事项／诉求等字段由 AI 单方面填进去。
 *       市民从头到尾没机会看一眼 AI 抽成了什么样。
 *
 * 改后：市民写完投诉内容及诉求 →「下一步」时打这条 `/api/citizen/prefill` →
 *       **1.6 秒**拿回 5 项 → 在确认页上核对、可改 → 连同提交体一起发上来。
 *       这五项在提交那一刻就是**用户确认过的值**，提交后那一趟 AI 不再碰它们。
 *
 * ════════ ★ 这一趟绝不能挡住提交 ════════
 *
 * 超时、模型跑飞、DeepSeek 挂了、密钥没配 —— **一律快速返回空结果**
 * （`status` 如实标明是哪一种），让前端把这几项留空且可填。
 * **它不是提交的前置条件**：确认页不许因为 prefill 没成功就不给按「提交」。
 *
 * ════════ 耗时实测（2026-08-27 本机，`deepseek-v4-flash`）════════
 *
 * **端到端走 HTTP 端点**，3 条不同长度的真实原文各跑 5 次（n=15）：
 *
 *     短 53 字    中位 1175ms   p90 1284ms   max 1284ms   0 失败
 *     中 269 字   中位  919ms   p90 1605ms   max 1605ms   0 失败
 *     长 363 字   中位  923ms   p90 1017ms   max 1017ms   0 失败
 *     ── 总体     中位  959ms   p90 1284ms   max 1605ms   0 失败
 *
 * 同一个模型跑整包提示词要 ~25 秒。**慢的根因不是模型，是输出 token 数**
 * （耗时 ≈ 输出 tokens ÷ 110 tok/s ＋ 2.2s 固定 API 开销）。
 *
 * ★★ **但只砍字段砍不到 1 秒。** 砍成 5 个短字段之后首轮实测仍是中位 3.9s、最慢 7.1s，
 *    而正文 JSON 本身只有 ~30 token —— 多出来的几百个 token **全是模型的自言自语**。
 *    真正把它压进 1 秒的是**关掉思考**（`reasoning_effort:'none'`）：
 *
 *      关思考前   中位 3.85s   p90 16.07s   max 25.13s   （n=18，抖成这样没法给用户看）
 *      关思考后   中位 0.97s   p90  1.37s   max  1.40s   （n=18，且输出逐次完全一致）
 *
 *    抽取质量没有下降 —— 两组给的 案件领域／区域／投诉事项／诉求 一致。
 *    完整实测账与「哪些写法被静默忽略」记在 `@cdfjx/structuring` 的 `deepseek.ts`
 *    （`ReasoningEffort`）与 `prefill.ts` 文件头，**改那一行之前先读完**。
 *
 * 默认超时给到 8 秒（实测 p90 的 6 倍），到点就空手返回，绝不无限等。
 *
 * ★ 已实测排除的岔路，别再重走：
 *   · DeepSeek 只有 flash / pro / flash-vision-exp 三个，**flash 已是最快**；
 *     pro 更慢（中位 3.51s）且这活上 9 条挂 8 条。
 *   · Moonshot 那把 key 是 `/coding/v1` 编程订阅接口，授权范围不对，不许用。
 *   · **系统提示词越短越稳**：入参 289 tokens 那版 9 条 0 失败；较长那版 15 条挂 1 条
 *     （模型在啰嗦案子上跑飞、输出撞上限被截断）。
 *
 * ★ **跑飞率仍按「存在」处理**：本轮 33 次调用 0 失败（关思考后输出短且稳定，
 *   撞不到上限），但这不是「不会失败」的证据 —— 上游超时、限流、密钥过期都还在。
 *   前端**必须**按五项全空也能提交来写，见下面那段★。
 */

// ── 路由 ────────────────────────────────────────────────────────────────────

/**
 * 提交前快速结构化。`POST`，请求体 `PrefillBody`，响应 `CitizenEnvelope<PrefillResult>`。
 *
 * ★ 与 Nest 装饰器里的字面量**必须逐字一致**（装饰器只吃字面量，改一处就要改两处）。
 */
export const CITIZEN_PREFILL_ROUTE = '/api/citizen/prefill';

/**
 * 区域下拉的选项来源。`GET`，响应 `CitizenEnvelope<AreaListResult>`。
 *
 * ★ 为什么必须有这条、而不让前端自己写死 24 个区：`区域ID` 是要**推老系统**的，
 *   填一个协会 `/api/SystemSet/GetArea` 列表外的值，推送当场失败。
 *   前端硬编码的那份迟早与协会侧对不上，而对不上时**没有任何报错**——
 *   只有一条推失败的脏记录。所以取值只有一个来源：这条接口。
 */
export const CITIZEN_AREAS_ROUTE = '/api/citizen/areas';

/**
 * 默认超时（毫秒）。实测 p90 才 2.13s，8 秒已经很宽 ——
 * 宽是**故意的**：这个数字不是用来卡性能的，是用来兜住「上游彻底不响应」那一种情形。
 * 可由环境变量 `PREFILL_TIMEOUT_MS` 覆盖（运维调参与兜底实测都用它）。
 */
export const PREFILL_TIMEOUT_MS = 8000;

// ── 取值白名单 ──────────────────────────────────────────────────────────────

/**
 * 「案件领域」的四个合法取值。
 *
 * ★ 这里是**真本**：`@cdfjx/structuring` 的 `CASE_DOMAINS` 现在直接引本常量，
 *   不再各写一份。两份枚举早晚会差一个字，而差一个字的表现是
 *   「模型给的值被静默丢掉、字段永远是空」—— 没有任何报错。
 */
export const PREFILL_CASE_DOMAINS = ['二手房', '新房', '租赁', '自媒体'] as const;
export type PrefillCaseDomain = (typeof PREFILL_CASE_DOMAINS)[number];

/** 两个一句话概括字段的字数上限。**前端据此提前拦，后端仍然独立再校验一次。** */
export const PREFILL_LIMITS = {
  /** 「投诉事项」一句话概括 */
  投诉事项Max: 20,
  /** 「诉求」一句话概括 */
  诉求Max: 20,
  /** 「房产标的」逐字抄原文，给宽一点 */
  房产标的Max: 60,
  /** 送去 prefill 的原文最短字数（与提交那道校验同一口径） */
  textMin: 10,
} as const;

// ── 出参形状 ────────────────────────────────────────────────────────────────

/**
 * 这一趟到底怎么收场的。**四个值，前端不许合并显示。**
 *
 * ★ 存在的理由与 `StructuringStatus` 同源，是本项目一条不许破的纪律：
 *   **「没跑成功」绝不能看起来像「本来就没内容」。**
 *   五项全空时，`ok` 意味着「模型看了原文，确实判不出来」，
 *   `timeout`/`failed` 意味着「压根没跑成」——前端可据此决定要不要提示一句
 *   「智能填充没跑成功，请手动填写」。但**无论哪一种，都必须让用户能继续提交**。
 *
 *   ok        跑完了。**ok 不等于字段都有值**——判不准就该留空，那是诚实不是失败。
 *   timeout   超过 `PREFILL_TIMEOUT_MS` 没回来，已放弃等待。
 *   failed    模型报错／输出解析不出来／密钥没配（约 7% 的跑飞率就落在这里）。
 *   skipped   压根没送去跑（原文太短等），不算失败。
 */
export type PrefillStatus = 'ok' | 'timeout' | 'failed' | 'skipped';

/**
 * 提交前快速结构化的产物 —— **就这五项**（`区域` 与 `区域ID` 是同一项的两个面）。
 *
 * ★★ **判不准一律空字符串，绝不编造**（全项目红线）。
 *    字符串字段的空值恒为 `''`，不是 `null`、不是 `undefined`、更不是「未知」这种占位词。
 *
 * ★ 为什么只有这五项、而不是原来的 11 项：**这五项是要给用户核对的**，
 *   多一项就多一份要用户在手机上读的东西，而其余几项要么是市民表单直接填的
 *   （被投诉方三项），要么是内部口径（违规标签／老系统两个枚举），
 *   拿给市民核对既看不懂也没意义。清单口径见
 *   `packages/server/src/modules/advice/structuring-contract.ts` 的删减注释。
 */
export interface PrefillResult {
  /** 二手房／新房／租赁／自媒体 之一；四类都不像恒为 `''` */
  案件领域: string;
  /**
   * 区域**中文名**，模型给什么就是什么（如「锦江区」）。
   *
   * ★ 注意它与 `区域ID` **可以不同步**：模型给了一个 GetArea 列表外的区名时，
   *   `区域` 有值而 `区域ID` 为 `null`。这是**故意保留**的信息 ——
   *   前端可以拿它去下拉里做预选提示，但**绝不能自己造一个 ID**。
   */
  区域: string;
  /**
   * 落老系统的 `AreaID`。**映射不上恒为 `null`，绝不瞎猜一个。**
   *
   * ★ 理由是硬的：这个值要推协会老系统，填一个列表外的数字**推送当场失败**，
   *   而老系统**没有删除接口**——推错了就是一条改不掉的脏记录。
   *   取值只能来自 `CITIZEN_AREAS_ROUTE` 给的那张表。
   */
  区域ID: number | null;
  /** 涉事房屋的位置描述，逐字抄原文；原文没说恒为 `''` */
  房产标的: string;
  /** 一句话概括投诉的是什么事，≤`PREFILL_LIMITS.投诉事项Max` 字 */
  投诉事项: string;
  /** 一句话概括投诉人要什么，≤`PREFILL_LIMITS.诉求Max` 字 */
  诉求: string;

  // ── 以下两项是**这一趟的运行情况**，不是抽取结果 ────────────────────────
  //    刻意用 ASCII 键名与上面五项的中文键名区分开，一眼能看出哪些是给用户核对的。

  /** 这一趟怎么收场的。见 `PrefillStatus` 的四个值 */
  status: PrefillStatus;
  /** 端到端耗时（毫秒），含超时那一种（那时约等于 `PREFILL_TIMEOUT_MS`） */
  ms: number;
}

/** 五项全空的结果。超时／跑飞／没送去跑，都走它 —— **一个字段都不编。** */
export function emptyPrefillResult(status: PrefillStatus, ms: number): PrefillResult {
  return {
    案件领域: '',
    区域: '',
    区域ID: null,
    房产标的: '',
    投诉事项: '',
    诉求: '',
    status,
    ms,
  };
}

/** 区域下拉的一项。`id` 就是要落进乐享「区域ID」、推老系统 `AreaID` 的那个数字。 */
export interface AreaOption {
  id: number;
  /**
   * 协会给的区域名。
   *
   * ★ 2026-08-27 实测：协会 `/api/SystemSet/GetArea` 返回的是**带后缀的全名**
   *   （`锦江区`／`都江堰市`／`金堂县`／`成都市外`）。
   *   注意 `@cdfjx/cdeaa` 的 `AreaItem.Name` 注释里写的是「青羊／成华」那种短名 ——
   *   **那条注释已经过时**，实测不是这样。这里记下实测值，别再照那条注释写映射逻辑。
   *
   * ★ 即便如此，映射仍然一律走 `normalizeAreaName` 归一，不做「反正都是全名」的假设：
   *   协会哪天把某一条改成短名（或市民自己敲了「锦江」），归一那条路照样能对上，
   *   而假设一旦不成立，表现是**下拉里选得到、映射却恒空**，一声不吭。
   *
   * ★ 前端**原样显示这个 `name`**，不要自己加或减后缀 —— 改了就与协会侧对不上账。
   */
  name: string;
}

export interface AreaListResult {
  items: AreaOption[];
  /**
   * 这张表是从哪来的：
   *   `cdeaa`    刚从协会 `/api/SystemSet/GetArea` 拉的（或缓存里那份拉回来的）
   *   `fallback` 协会接口这会儿不通，用的是**实测快照**（24 条，2026-08-24 落的）
   *
   * ★ 为什么要如实说：快照可能已经过期。前端不必因此变样，
   *   但排障时「下拉里少了一个新区」这种问题，第一眼要能看出走的是哪条路。
   */
  source: 'cdeaa' | 'fallback';
}

// ── 入参形状 ────────────────────────────────────────────────────────────────

/** `POST /api/citizen/prefill` 的请求体。**只有原文，不夹带任何身份参数。** */
export interface PrefillBody {
  /** 市民打的那段字（就是提交体里的「原文」，此刻还没提交） */
  text: string;
}

/**
 * 用户在确认页核对后的那五项 —— `CitizenSubmitBody` 从本接口继承它们。
 *
 * ★ **全部可选**：prefill 没跑成、或用户懒得填，提交照样成立（见文件头★）。
 * ★ 后端仍然独立校验一遍（枚举白名单、字数、区域 ID 在不在表里）：
 *   前端拦一道是体验，**闸门在服务端**——绕过页面直接打接口照样过不去。
 */
export interface CitizenConfirmedFields {
  /** 必须是 `PREFILL_CASE_DOMAINS` 之一，否则 400 */
  案件领域?: string;
  /** 必须在 `CITIZEN_AREAS_ROUTE` 给的表里，否则 400。**绝不静默改成一个别的值** */
  区域ID?: number;
  房产标的?: string;
  投诉事项?: string;
  诉求?: string;
}

/** 用户确认后**真正会被落库**的那几个键，逐字写死一份，供两端遍历用。 */
export const CITIZEN_CONFIRMED_FIELD_NAMES = [
  '案件领域',
  '区域ID',
  '房产标的',
  '投诉事项',
  '诉求',
] as const;

export type CitizenConfirmedFieldName = (typeof CITIZEN_CONFIRMED_FIELD_NAMES)[number];

// ── 区域名归一（前后端共用，避免两边各写一套「要不要加区字」）────────────────

/**
 * 区域名归一：吃掉「成都市」前缀与「区／县／市」后缀的差异，得到一个**比对键**。
 *
 * 为什么必须有它：协会 `/api/SystemSet/GetArea` 实测给的是**短名**（「青羊」「成华」），
 * 模型给的是「青羊区」，市民嘴里可能是「成都市青羊区」——
 * 这三个不归到同一个键上，映射就恒空、`区域ID` 永远填不上，而且**一声不吭**。
 *
 * ★ 两处坑，都是踩出来的，改这个函数前先看：
 *
 *   ① **「成都市外」不许被前缀规则吃掉。** 它是 GetArea 里第 24 条（AreaID=155），
 *      直接 `replace(/^成都市?/,'')` 会把它变成「外」。所以前缀只在
 *      **剩下的部分还有 2 个字以上**时才剥 —— 「成都市外」剥完只剩 1 个字，不剥。
 *
 *   ② **后缀要反复剥，不能只剥一次、更不能把「新区」当一个整体去交替匹配。**
 *      `/(区|县|市|新区)$/` 里 `区` 排在前面会先命中，「天府新区」变成「天府新」，
 *      而如果协会那边给的是「天府新区」、我们这边给的是「天府新」，两边就对不上。
 *      反复剥到剥不动为止，「天府新区」与「天府新」都收敛到「天府新」，两边必然一致。
 *
 * ★ 只做**归一**，不做猜测：归一之后仍对不上表里任何一条，那就是对不上 ——
 *   上层据此把 `区域ID` 判成 `null`，**绝不挑一个最像的顶上**。
 */
export function normalizeAreaName(name: string | undefined | null): string {
  if (!name) return '';
  let s = String(name).trim().replace(/\s+/g, '');
  if (!s) return '';
  // ① 前缀：只在剥完还剩 ≥2 字时剥，护住「成都市外」
  const withoutPrefix = s.replace(/^成都市?/, '');
  if (withoutPrefix.length >= 2) s = withoutPrefix;
  // ② 后缀：反复剥到剥不动，护住「天府新区」↔「天府新」这类长短名并存
  for (;;) {
    const next = s.replace(/(区|县|市)$/, '');
    if (next === s || next.length < 2) break;
    s = next;
  }
  return s;
}
